import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Users, Heart, Award, ShieldCheck, Megaphone, Compass,
  ArrowRight, Check, X, Zap, Crown, HandHeart, Building2,
  ChevronDown, TrendingUp, DollarSign, Briefcase,
  Mic, Video, MessageSquare, Camera, Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import SeoMeta from "@/components/SeoMeta";
import OnlyBestLeaderboardWidget from "@/components/OnlyBestLeaderboardWidget";
import OnlyBestCreatorOfMonthSpotlight from "@/components/OnlyBestCreatorOfMonthSpotlight";

/**
 * OnlyBest by LocalBest.ai — campaign landing page.
 * Lives at /onlybest. Reuses the existing dark LocalBest theme with extra gold
 * accents to evoke a "movement" feel without departing from the brand.
 */
export default function OnlyBestPage() {
  return (
    <div className="min-h-screen bg-[#070B16] text-white" data-testid="onlybest-page">
      <SeoMeta
        title="OnlyBest Creators — Get Paid to Promote the Best Local Businesses"
        description="Introduce the local business owners you know, book them a strategy call with Roe Hubbard, and earn 20% of their first month plus 10% every month they stay active."
        canonical="https://localbest.ai/onlybest"
        ogTitle="OnlyBest Creators — Get Paid to Promote the Best Local Businesses"
        ogDescription="Recommend, book, earn. 20% first month, 10% recurring. Certified Creators earn 40% on first months."
        ogImage="https://localbest.ai/assets/localbest-verified-badge.png"
      />

      {/* === Hero: Streaming Creator Marquee (One creator, every category) === */}
      <HeroMarquee />

      {/* === Cinematic Banner (was hero) — now section 2 === */}
      <CinematicBanner />

      {/* === Be the Face: Avatar / Spokesperson / Talking Widget — section 3 === */}
      <BeTheFace />

      {/* === Social proof / movement strip === */}
      <MovementStrip />

      {/* === A Better Creator Economy === */}
      <BetterCreatorEconomy />

      {/* === Manifesto === */}
      <Manifesto />

      {/* === How It Works === */}
      <HowItWorks />

      {/* === Who It's For === */}
      <WhoItsFor />

      {/* === What You Can Promote === */}
      <WhatYouCanPromote />

      {/* === Creator Program Details (overview, pricing, payouts, examples, support) === */}
      <CreatorProgramDetails />

      {/* === Creator of the Month cinematic spotlight (TV frame) === */}
      <OnlyBestCreatorOfMonthSpotlight />

      {/* === Top OnlyBest Creators leaderboard widget === */}
      <OnlyBestLeaderboardWidget />

      {/* === OnlyBest Streamer teaser === */}
      <OnlyBestStreamerTeaser />

      {/* === Business Affiliate Section === */}
      <BusinessAffiliate />

      {/* === Cause / Community Impact === */}
      <CauseImpact />

      {/* === FAQ === */}
      <Faq />

      {/* === Disclosures (compliance block) === */}
      <Disclosures />

      {/* === Final CTA === */}
      <FinalCta />
    </div>
  );
}

/* ---------------- HERO (MARQUEE) ---------------- */
function HeroMarquee() {
  // Reuse the 26 OnlyBest Creator photos as the immersive backdrop
  const images = Array.from({ length: 26 }, (_, i) => `/assets/onlybest-creators/obc-${String(i + 1).padStart(2, "0")}.jpg`);
  const rowA = [...images, ...images];
  const rowB = [...images].reverse().concat([...images].reverse());

  return (
    <section className="relative overflow-hidden pt-24 pb-16" data-testid="onlybest-hero">
      {/* Soft red/blue glow behind copy */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(220,38,38,0.10),transparent_55%),radial-gradient(circle_at_75%_60%,rgba(37,99,235,0.12),transparent_55%)]" />

      {/* Headline + CTAs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs sm:text-sm font-bold tracking-[0.18em] text-[#DC2626] uppercase mb-4">
            OnlyBest by LocalBest.ai
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-[1.05] tracking-tight text-white">
            Make money{" "}
            <span className="bg-gradient-to-r from-[#DC2626] via-white to-[#2563EB] bg-clip-text text-transparent">
              promoting the best
            </span>
            {" "}— without selling yourself.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl mx-auto">
            Become the face of the Best of Best businesses in your community. We turn your
            photos into a hyper-realistic avatar, put you on their listing, and pay you for
            every recommendation, asset, and connection you create.
          </p>
          <p className="mt-4 text-sm sm:text-base text-[#64748B] leading-relaxed max-w-2xl mx-auto italic">
            One creator. Every category. Real income. Real impact.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/onlybest/apply" data-testid="hero-create-account-btn">
              <Button size="lg" className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white font-semibold rounded-full px-7 py-6 text-base shadow-lg shadow-[#2563EB]/30">
                <Sparkles className="w-4 h-4 mr-2" /> Create My OnlyBest Account
              </Button>
            </Link>
            <Link to="/submit?nominate=1" data-testid="hero-nominate-btn">
              <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full px-7 py-6 text-base">
                <Award className="w-4 h-4 mr-2" /> Nominate a Business
              </Button>
            </Link>
            <Link to="/leaderboard" data-testid="hero-leaderboard-btn">
              <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full px-7 py-6 text-base">
                <Crown className="w-4 h-4 mr-2" /> See the Leaderboard
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Streaming OnlyBest Creator marquee — the visual heartbeat of the page */}
      <div className="mt-14 space-y-4 sm:space-y-5">
        <MarqueeRow images={rowA} direction="left" duration={70} />
        <MarqueeRow images={rowB} direction="right" duration={85} />
      </div>

      <p className="mt-10 text-center text-xs sm:text-sm text-[#64748B] italic px-4">
        Every photo above is the same creator. With our training, tools & AI Horse-Power™ systems,
        you can step into <span className="text-white">any</span> Best of Best business your community needs.
      </p>
    </section>
  );
}

/* ---------------- CINEMATIC BANNER (was the old hero, now section 2) ---------------- */
function CinematicBanner() {
  return (
    <section className="relative overflow-hidden border-t border-white/5" data-testid="onlybest-banner">
      {/* Cinematic banner image (edge-to-edge) */}
      <div className="relative w-full">
        <motion.img
          initial={{ opacity: 0, scale: 1.04 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          src="/assets/onlybest_hero.png"
          alt="OnlyBest Creator — Promote good. Earn more. Change lives."
          className="w-full h-auto max-h-[860px] object-contain object-top"
          loading="lazy"
          data-testid="hero-image"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#070B16]" />
      </div>

      {/* Supporting copy below the cinematic banner */}
      <div className="relative">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(220,38,38,0.08),transparent_55%),radial-gradient(circle_at_75%_60%,rgba(37,99,235,0.10),transparent_55%)]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs sm:text-sm font-bold tracking-[0.18em] text-[#F4B41A] uppercase mb-3">
              Promote good. Earn more. Change lives.
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] leading-[1.1] tracking-tight text-white">
              Turn your taste, trust, and community into{" "}
              <span className="bg-gradient-to-r from-[#F4B41A] to-[#E0A015] bg-clip-text text-transparent">
                real income.
              </span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-[#94A3B8] leading-relaxed max-w-2xl mx-auto italic">
              The internet told people they had to chase attention, compromise their values,
              or become the product to make money online. OnlyBest gives people a better way —
              recommend trusted local businesses, causes, creators, churches, schools, events,
              deals, and community favorites, and turn every recommendation into income and impact.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- LEGACY HERO (kept for reference; no longer used) ---------------- */
function Hero() {
  return (
    <section className="relative overflow-hidden pt-20" data-testid="onlybest-hero">
      {/* === Cinematic banner image (edge-to-edge) === */}
      <div className="relative w-full">
        <motion.img
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          src="/assets/onlybest_hero.png"
          alt="OnlyBest Creator — Join the Revolution. Promote good. Earn more. Change lives."
          className="w-full h-auto max-h-[860px] object-contain object-top"
          loading="eager"
          data-testid="hero-image"
        />
        {/* Subtle bottom fade so the banner bleeds into the dark page */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#070B16]" />
      </div>

      {/* === Hero copy + CTAs (below the banner) === */}
      <div className="relative">
        {/* Soft red/blue glow behind copy */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(220,38,38,0.10),transparent_55%),radial-gradient(circle_at_75%_60%,rgba(37,99,235,0.12),transparent_55%)]" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-16 sm:pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-[1.05] tracking-tight text-white">
              Make money{" "}
              <span className="bg-gradient-to-r from-[#DC2626] via-white to-[#2563EB] bg-clip-text text-transparent">
                promoting the best
              </span>
              {" "}— without selling yourself.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl mx-auto">
              Join the OnlyBest Creator Program and earn income by recommending trusted
              businesses, causes, creators, churches, schools, events, deals, and
              community opportunities.
            </p>
            <p className="mt-5 text-sm sm:text-base text-[#64748B] leading-relaxed max-w-2xl mx-auto italic">
              The internet told people they had to chase attention, compromise their
              values, or become the product to make money online. OnlyBest gives people a
              better way. Turn your taste, trust, community influence, and local
              recommendations into income and impact.
            </p>

            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Link to="/onlybest/apply" data-testid="hero-create-account-btn">
                <Button size="lg" className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white font-semibold rounded-full px-7 py-6 text-base shadow-lg shadow-[#2563EB]/30">
                  <Sparkles className="w-4 h-4 mr-2" /> Create My OnlyBest Account
                </Button>
              </Link>
              <Link to="/submit?nominate=1" data-testid="hero-nominate-btn">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full px-7 py-6 text-base">
                  <Award className="w-4 h-4 mr-2" /> Nominate a Business
                </Button>
              </Link>
              <Link to="/leaderboard" data-testid="hero-leaderboard-btn">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full px-7 py-6 text-base">
                  <Crown className="w-4 h-4 mr-2" /> See the Leaderboard
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- MOVEMENT STRIP ---------------- */
function MovementStrip() {
  const stats = [
    { v: "100%", l: "Values-Based" },
    { v: "0%", l: "Selling Yourself" },
    { v: "∞", l: "Local Impact" },
    { v: "1", l: "National Movement" },
  ];
  return (
    <section className="border-y border-white/5 bg-[#0B1226]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.l} className="min-w-0">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#DC2626] to-[#2563EB] bg-clip-text text-transparent">{s.v}</div>
              <div className="text-xs sm:text-sm text-[#94A3B8] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- BETTER CREATOR ECONOMY ---------------- */
function BetterCreatorEconomy() {
  const old = [
    "Sell access to yourself.",
    "Chase attention.",
    "Build fans.",
    "Post more.",
    "Compromise more.",
    "Become the product.",
  ];
  const ours = [
    "Promote what you believe in.",
    "Build trust.",
    "Help your community.",
    "Support local businesses, professionals, charities, churches, artists, musicians, events & startups.",
    "Share real recommendations.",
    "Earn from impact.",
  ];
  return (
    <section className="py-20" data-testid="better-economy-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="A Better Creator Economy"
          title="The internet sold creators a lie."
          subtitle="We are not here to judge people. We are here to build a better path."
        />

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a1d2e] to-[#0F172A] p-6 min-w-0">
            <div className="flex items-center gap-2 mb-4">
              <X className="w-5 h-5 text-[#EF4444]" />
              <h3 className="text-lg sm:text-xl font-bold text-white">The Old Internet Says</h3>
            </div>
            <ul className="space-y-2.5">
              {old.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm sm:text-base text-[#94A3B8]">
                  <span className="w-1 h-1 rounded-full bg-[#EF4444]/70 mt-2 flex-shrink-0" />
                  <span className="line-through decoration-[#EF4444]/40">{l}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-[#DC2626]/30 bg-gradient-to-br from-[#DC2626]/8 to-[#2563EB]/5 p-6 min-w-0">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-[#DC2626]" />
              <h3 className="text-lg sm:text-xl font-bold text-white">OnlyBest Says</h3>
            </div>
            <ul className="space-y-2.5">
              {ours.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm sm:text-base text-white">
                  <Check className="w-4 h-4 text-[#DC2626] mt-0.5 flex-shrink-0" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- MANIFESTO ---------------- */
function Manifesto() {
  const lines = [
    "We believe people should be able to make money online without losing themselves.",
    "We believe influence should build families, businesses, churches, schools, causes, and communities.",
    "We believe local businesses, professionals, charities, churches, artists, musicians, actors, models, startups and events deserve more attention than empty trends.",
    "We believe creators should be rewarded for trust, not pressure.",
    "We believe recommendations are powerful.",
    "We believe good taste, good values, and good influence should pay.",
    "We believe America needs more community builders.",
    "We believe there is a better way.",
  ];
  return (
    <section className="py-20 relative" data-testid="manifesto-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(124,58,237,0.10),transparent_60%)]" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.3em] text-[#DC2626] mb-3 text-center">The OnlyBest Manifesto</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center font-['Outfit'] mb-10">
          We believe there is a <span className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] bg-clip-text text-transparent">better way</span>.
        </h2>
        <div className="space-y-3">
          {lines.map((l, i) => (
            <motion.p
              key={l}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="text-base sm:text-lg text-[#E2E8F0] leading-relaxed"
            >
              <span className="text-[#DC2626] mr-2">—</span>
              {l}
            </motion.p>
          ))}
        </div>
        <p className="mt-10 text-center text-white text-lg sm:text-xl font-semibold">
          Welcome to <span className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] bg-clip-text text-transparent">OnlyBest by LocalBest.ai.</span>
        </p>
        <div className="mt-8 text-center">
          <Link to="/onlybest/apply">
            <Button size="lg" className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white rounded-full px-7 py-6" data-testid="manifesto-cta">
              Join the Movement <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */
function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Recommend",
      body: "Introduce a business owner you know: a restaurant, contractor, salon, agency, shop, or service company. One honest intro is enough.",
      icon: Compass,
    },
    {
      n: "02",
      title: "Book",
      body: "They apply for $1 using your link, then book a strategy call with Roe Hubbard, Fractional Chief AI & Marketing Officer. Roe and the team handle the close.",
      icon: Sparkles,
    },
    {
      n: "03",
      title: "Earn & Create",
      body: "Earn 20% of their first collected month and 10% every month they stay active. Get paid to create their videos and posts if you want.",
      icon: Crown,
    },
  ];
  return (
    <section className="py-20 border-t border-white/5 bg-[#0B1226]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading kicker="How It Works" title="Three simple steps. One honest intro." />
        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="relative rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-[#DC2626]/30 transition min-w-0">
                <span className="absolute -top-3 -right-3 w-10 h-10 rounded-xl bg-gradient-to-br from-[#DC2626] to-[#2563EB] text-white flex items-center justify-center text-xs font-bold">{s.n}</span>
                <Icon className="w-7 h-7 text-[#DC2626] mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <Link to="/onlybest/apply">
            <Button size="lg" className="bg-gradient-to-r from-[#1E40AF] to-[#2563EB] hover:opacity-90 text-white rounded-full px-7 py-6">
              Start Recommending the Best <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- BE THE FACE (avatar / spokesperson / talking widget) ---------------- */
function BeTheFace() {
  return (
    <section className="relative py-24 border-t border-white/5 overflow-hidden" data-testid="be-the-face-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/[0.10] via-transparent to-[#F4B41A]/[0.08] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="Become the Face of a Best of Best Business"
          title="You're the avatar. You're the spokesperson. You're the talking widget on their listing."
          subtitle="Every business you recommend gets a Best of Best listing — and you become their on-screen AI Hero. Visitors see YOU, hear YOU, chat with YOU. You don't sell. You represent. And every connection pays you."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: explanation */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">Become the on-screen avatar</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">
                  We turn your photos into a hyper-realistic avatar that lives on the business&apos;s listing —
                  same style as the talking AI sales agents you&apos;ve seen on
                  <a href="https://aihpower.com/nox-ai-sales-agent" target="_blank" rel="noopener noreferrer"
                     className="text-[#A78BFA] hover:text-[#C4B5FD] ml-1">aihpower.com/nox-ai-sales-agent</a>.
                  Visitors see you smiling, waving, talking — like you walked into their living room.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">Chat, video and audio calls — all you</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">
                  The listing&apos;s chat, video call and audio call buttons all route to your trained AI Hero
                  voice and face. Visitors get a real &ldquo;talk to a local&rdquo; experience 24/7 — and you don&apos;t
                  have to be online.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#F4B41A] to-[#E0A015] flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-[#0A0F1C]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">Promote them everywhere — images, reels, ads</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">
                  We give you the tools to spin out images, short videos, cinematic ads, reels, postcards,
                  email templates and social posts — all featuring YOU representing that business across
                  the community. You get paid for every asset you choose to deploy.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">Lift your community while you earn</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">
                  Your business gets picked as <span className="text-[#F4B41A] font-semibold">Best of Best</span>,
                  and may receive up to <span className="text-[#10B981] font-semibold">$120,000 in Business Co-Op Credits applied toward AI Horse-Powered services</span>,
                  while getting promoted locally by you and the entire OnlyBest network. More revenue stays in
                  YOUR marketplace, more neighbors get a real shot to earn the right way.
                </p>
              </div>
            </div>

            <div className="pt-2">
              <Link to="/onlybest/apply">
                <Button size="lg" className="bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-bold rounded-full px-7">
                  Become an OnlyBest Creator <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-[#64748B] mt-3">
                Full training, tools, and systems provided. No prior experience needed.
              </p>
            </div>
          </div>

          {/* Right: mock listing widget showing the talking-avatar */}
          <div className="relative mx-auto w-full max-w-md">
            <div className="relative rounded-3xl overflow-hidden bg-[#0F172A] border border-white/10 shadow-2xl">
              <img
                src="/assets/onlybest-creators/obc-02.jpg"
                alt="OnlyBest Creator representing an auto dealership"
                className="w-full aspect-[9/14] object-cover"
              />
              {/* Top tag */}
              <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white border border-white/20">
                  ● LIVE — Talking now
                </span>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-[#7C3AED]/90 text-white border border-white/20" data-testid="ai-assistant-badge">
                  AI Assistant
                </span>
              </div>
              {/* Bottom CTA strip — mimics the Nox / on-listing widget */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-4 pt-12">
                <p className="text-white text-sm font-semibold mb-3 drop-shadow">
                  Get a Free Quote — From your Best of Best Dealer
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <button className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 transition">
                    <Video className="w-4 h-4 text-white" />
                    <span className="text-[10px] text-white font-medium">Video Call</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 transition">
                    <Mic className="w-4 h-4 text-white" />
                    <span className="text-[10px] text-white font-medium">Audio Call</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 transition">
                    <MessageSquare className="w-4 h-4 text-white" />
                    <span className="text-[10px] text-white font-medium">Start Chat</span>
                  </button>
                </div>
                <p className="text-[10px] text-white/60 text-center mt-2">
                  Powered by AI Horse-Power™
                </p>
              </div>
            </div>
            <p className="text-center text-xs text-[#64748B] mt-3 italic">
              AI-generated avatar. This is how visitors see you on every listing you represent.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- MARQUEE ROW (used by HeroMarquee) ---------------- */
function MarqueeRow({ images, direction = "left", duration = 60 }) {
  return (
    <div className="relative overflow-hidden">
      {/* Edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#070B16] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#070B16] to-transparent z-10" />
      <div
        className={`flex gap-4 w-max ${direction === "left" ? "animate-obc-marquee-left" : "animate-obc-marquee-right"}`}
        style={{ animationDuration: `${duration}s` }}
      >
        {images.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className="relative flex-shrink-0 w-[160px] sm:w-[200px] md:w-[220px] aspect-[9/16] rounded-2xl overflow-hidden bg-[#0F172A] border border-white/10 shadow-lg group"
          >
            <img
              src={src}
              alt="OnlyBest Creator example pose"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {/* Subtle bottom tag */}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-white/90">
                <Sparkles className="w-3 h-3 text-[#F4B41A]" /> OnlyBest Creator
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- WHO IT'S FOR ---------------- */
function WhoItsFor() {
  const tags = [
    "Stay-at-home moms", "College students", "Local influencers", "Business owners' spouses & family",
    "Church members", "Youth sports parents", "Coaches", "Teachers", "Community leaders",
    "Artists & musicians", "Realtors & loan officers", "Foodies", "Barbers, stylists & salon pros",
    "Local event promoters", "Veterans", "Retirees", "Side hustlers",
    "Anyone who wants more income & impact",
  ];
  return (
    <section className="py-20" data-testid="who-its-for-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="Who Can Become an OnlyBest Creator?"
          title="If people already ask you where to eat, who to hire, what to buy, where to go, or who to trust — you can become an OnlyBest Creator."
        />
        <div className="mt-10 flex flex-wrap gap-2 justify-center max-w-4xl mx-auto">
          {tags.map((t) => (
            <span key={t} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-sm text-[#E2E8F0] hover:bg-[#DC2626]/10 hover:border-[#DC2626]/30 transition">
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHAT YOU CAN PROMOTE ---------------- */
function WhatYouCanPromote() {
  const cats = [
    { icon: Building2, label: "Local Businesses" }, { icon: Heart, label: "Causes & Charities" },
    { icon: Crown, label: "Best of Best Picks" }, { icon: Megaphone, label: "Events" },
    { icon: HandHeart, label: "Churches & Schools" }, { icon: Award, label: "Artists & Creators" },
    { icon: ShieldCheck, label: "Trusted Services" }, { icon: Sparkles, label: "Deals & Rewards" },
  ];
  return (
    <section className="py-20 border-t border-white/5 bg-[#0B1226]" data-testid="promote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading kicker="What You Can Promote" title="Recommend the best — across every category." />
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {cats.map(({ icon: I, label }) => (
            <div key={label} className="rounded-xl bg-white/[0.03] border border-white/10 p-5 text-center hover:border-[#DC2626]/30 transition min-w-0">
              <I className="w-6 h-6 text-[#DC2626] mx-auto mb-2" />
              <p className="text-sm text-white font-medium">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOW YOU CAN EARN ---------------- */
function HowYouCanEarn() {
  const items = [
    { title: "Referral Rewards", body: "Earn when businesses you refer join LocalBest.ai or upgrade to paid plans." },
    { title: "Affiliate Commissions", body: "Trackable links to businesses, products, and services tied to your creator ID." },
    { title: "Points & Badges", body: "Climb the OnlyBest leaderboard with every click, share, lead, and signup." },
    { title: "Business Partnerships", body: "Direct sponsorships, ambassador deals, and ongoing campaigns with your favorite local brands." },
  ];
  return (
    <section className="py-20" data-testid="earn-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading kicker="How You Can Earn" title="Multiple income streams, one creator page." />
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {items.map((it) => (
            <div key={it.title} className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-[#DC2626]/30 transition min-w-0">
              <h3 className="text-white font-semibold text-lg mb-2 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#DC2626]" /> {it.title}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#64748B] mt-8 max-w-2xl mx-auto leading-relaxed">
          OnlyBest Creator pages may include sponsored recommendations, affiliate links, paid partnerships, or referral-based promotions. Creators and businesses are expected to be transparent, truthful, and community-first. Income opportunities are not guaranteed.
        </p>
      </div>
    </section>
  );
}

/* ---------------- INCOME POTENTIAL ---------------- */
function IncomePotential() {
  return (
    <section className="py-20" data-testid="income-potential-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="Huge Creator Income Potential — Done With Dignity"
          title="A new path to make real money — by promoting the best, not yourself."
          subtitle="Other creator platforms have proven people can earn serious money online. OnlyBest creates a cleaner way: build income by promoting trusted businesses, causes, communities, offers, and opportunities you actually believe in."
        />

        {/* Bold-but-compliant statement */}
        <div className="mt-12 max-w-4xl mx-auto rounded-3xl border border-[#DC2626]/30 bg-gradient-to-r from-[#DC2626]/10 via-[#0F172A] to-[#2563EB]/10 p-6 sm:p-8">
          <div className="flex items-start gap-4 flex-wrap">
            <span className="inline-flex w-11 h-11 rounded-2xl bg-gradient-to-br from-[#DC2626] to-[#2563EB] items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-white" />
            </span>
            <p className="text-white text-base sm:text-lg leading-relaxed flex-1 min-w-0 font-medium">
              There is <span className="text-[#DC2626]">no set ceiling</span> on what an
              action-taking creator can build. OnlyBest gives creators a cleaner,
              community-powered way to pursue major income potential by promoting
              the best businesses, causes, and opportunities — instead of selling
              themselves.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- BUSINESS AFFILIATE ---------------- */
function BusinessAffiliate() {
  const benefits = [
    "Let local creators promote your business",
    "Give customers trackable links and QR codes",
    "Reward referrals, leads, calls, bookings, and sales",
    "Recruit community ambassadors",
    "Turn family members into promoters",
    "Connect offers to your CRM",
    "Track leads, forms, calls, rewards, and campaign performance",
    "Build your own local affiliate army",
  ];
  return (
    <section className="py-20 border-t border-white/5 bg-gradient-to-br from-[#0B1226] via-[#070B16] to-[#0B1226]" data-testid="business-affiliate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.3em] text-[#DC2626] mb-3">For Business Owners</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
            Turn your customers, fans, family, and community into <span className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] bg-clip-text text-transparent">promoters</span>.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#94A3B8] leading-relaxed">
            LocalBest.ai helps businesses create a stronger referral engine by giving creators, affiliates, family members, customers, influencers, and community supporters a simple way to promote them.
          </p>
          <p className="mt-3 text-sm text-[#64748B] italic">
            &ldquo;My wife, kids, staff, customers, church friends, and local supporters can all have OnlyBest Creator pages and promote my business with trackable links.&rdquo;
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/submit">
              <Button className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white rounded-full px-5">
                Start My Business Affiliate Program
              </Button>
            </Link>
            <Link to="/submit">
              <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-5">
                Create My Best of Best Listing
              </Button>
            </Link>
          </div>
        </div>
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 min-w-0">
          <ul className="space-y-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-[#E2E8F0]">
                <Check className="w-4 h-4 text-[#DC2626] mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CAUSE IMPACT ---------------- */
function CauseImpact() {
  return (
    <section className="py-20" data-testid="cause-impact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section banner — community impact lifestyle visual */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-[#2563EB]/10 mb-12"
          data-testid="cause-impact-banner"
        >
          <img
            src="/assets/onlybest_banner.png"
            alt="OnlyBest — make money promoting the best, strengthen your community"
            className="w-full h-auto block"
            loading="lazy"
          />
          {/* Soft gradient overlay to blend into the page */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#070B16] to-transparent pointer-events-none" />
        </motion.div>

        <div className="max-w-4xl mx-auto text-center">
          <SectionHeading
            kicker="Income + Impact"
            title="Promote what you believe in. Support what matters."
            subtitle="Creators can choose to support causes, churches, schools, youth sports, charities, and community projects through their recommendations and campaigns."
          />
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <ImpactBlock icon={HandHeart} title="For Yourself" body="Build side income from your local influence." />
            <ImpactBlock icon={Heart} title="For a Cause" body="Direct rewards to your church, school, or favorite charity." />
            <ImpactBlock icon={Users} title="For Both" body="Split income between yourself and the causes you support." />
          </div>
        </div>
      </div>
    </section>
  );
}
function ImpactBlock({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-[#DC2626]/30 transition min-w-0">
      <Icon className="w-8 h-8 text-[#DC2626] mx-auto mb-3" />
      <h3 className="text-white font-semibold mb-1">{title}</h3>
      <p className="text-sm text-[#94A3B8]">{body}</p>
    </div>
  );
}

/* ---------------- FAQ ---------------- */
function Faq() {
  const items = [
    { q: "Do I have to sell?", a: "No. You recommend and book the call; Roe Hubbard and the team close." },
    { q: "When do I get paid?", a: "Monthly, on collected payments, after a 30-day hold." },
    { q: "How long do I earn?", a: "10% every month the business stays active." },
    { q: "What is a Certified Creator?", a: "Complete certification + 3 activated businesses to unlock 40% on first months." },
    { q: "Do I need followers?", a: "No. Relationships matter more than follower count." },
    { q: "Is this adult content?", a: "Never. OnlyBest is 100% about promoting great businesses." },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="py-20 border-t border-white/5 bg-[#0B1226]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading kicker="FAQ" title="Quick answers." />
        <div className="mt-10 space-y-3">
          {items.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={it.q} className="rounded-xl border border-white/10 bg-white/[0.03]">
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={open}
                >
                  <span className="text-white font-medium">{it.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#94A3B8] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="px-5 pb-4 text-sm text-[#94A3B8] leading-relaxed">{it.a}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCta() {
  return (
    <section className="py-24 relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(251,191,36,0.20),transparent_60%),radial-gradient(circle_at_50%_70%,rgba(236,72,153,0.15),transparent_60%)]" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
          Your community already trusts your opinion.
        </h2>
        <p className="mt-3 text-xl sm:text-2xl text-[#DC2626] font-semibold">
          Now your recommendations can create income.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/onlybest/apply">
            <Button size="lg" className="bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white rounded-full px-7 py-6 shadow-lg shadow-[#2563EB]/20" data-testid="final-create-cta">
              <Sparkles className="w-4 h-4 mr-2" /> Create My OnlyBest Account
            </Button>
          </Link>
        </div>
        <p className="mt-6 text-xs text-[#64748B] max-w-xl mx-auto">
          OnlyBest is built for people who believe income should not cost their identity, values, peace, or future.
        </p>
      </div>
    </section>
  );
}

/* ---------------- CREATOR PROGRAM DETAILS ---------------- */
function CreatorProgramDetails() {
  const responsibilities = [
    "Recommending businesses & agents to LocalBest.ai",
    "Helping them apply for Best of Best approval",
    "Promoting their LocalBest.ai Listing",
    "Sharing their offers, events, videos, and incentives",
    "Creating or coordinating weekly content posts",
    "Helping with reels, shorts, and video creatives",
    "Helping create their Nox AI Sales Agent avatar and content",
    "Updating listing content, offers, gallery, links, and media",
    "Helping with lead follow-up and order requests",
    "Promoting affiliate campaigns when available",
    "Supporting Best of Best Live events, cinematic ads, and local promotions",
  ];

  const freeIncludes = [
    "Your own dashboard login",
    "Your own OnlyBest Creator Listing on LocalBest.ai",
    "Your own referral tracking links",
    "Creator resources, scripts, and promotional materials",
    "Access to recommend businesses and agents",
    "Opportunity to earn commissions from approved clients you refer",
  ];

  const supportItems = [
    "Your own OnlyBest Creator Listing",
    "Dashboard login & referral tracking links",
    "Creator scripts & talking points",
    "Business invitation templates",
    "Social media post ideas",
    "Local promotion guidance",
    "Training on how to recommend businesses",
    "Training on how to explain Best of Best",
    "Support from the LocalBest.ai & AI Horse-Powered team",
    "Tools & resources unlocked with 1+ active client",
    "Client onboarding, listing setup, CRM connection, order flow support",
    "Work directly with the client's Listing, Nox AI Sales Agent, content, offers, and campaigns when assigned",
  ];

  const whoFor = [
    "Content creators",
    "Affiliate marketers",
    "Local influencers",
    "Freelancers",
    "Sales professionals",
    "Community connectors",
    "Event promoters",
    "Online publishers",
    "Virtual assistants",
    "Social media managers",
    "Photographers & videographers",
    "Spokespersons & hosts",
    "Business consultants",
    "People who already know great local businesses",
  ];

  return (
    <section
      className="py-20 border-t border-white/5 bg-gradient-to-b from-[#070B16] via-[#0B1226] to-[#070B16]"
      data-testid="creator-program-details-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HERO BLOCK */}
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] mb-3">
            The Full Creator Program
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
            Become an OnlyBest Creator
          </h2>
          <p className="mt-4 text-lg sm:text-xl text-[#E2E8F0]">
            Promote Good. Earn More. Help Local Businesses Win.
          </p>
          <p className="mt-5 text-base text-[#94A3B8] leading-relaxed">
            OnlyBest Creators are local promoters, content creators, affiliate
            marketers, online publishers, spokespersons, freelancers, and business
            support partners who help introduce great local businesses, professionals,
            charities, churches, artists, musicians, actors, models, startups and events
            to the LocalBest.ai and AI Horse-Powered platform.
          </p>
          <p className="mt-4 text-base text-[#94A3B8] leading-relaxed">
            You can start <span className="text-white font-semibold">free</span>.
            There is no required monthly fee. Once approved you receive your own
            Creator dashboard login and your own OnlyBest Creator Listing on
            LocalBest.ai so you can promote yourself, your services, your
            referrals, and your local business network.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/onlybest/apply" data-testid="creator-program-join-free-top">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold rounded-full px-7 py-6 text-base shadow-lg shadow-[#F59E0B]/30"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Join Free
              </Button>
            </Link>
          </div>
        </div>

        {/* WHAT YOU DO */}
        <div className="mt-16">
          <SectionHeading
            kicker="What You Do as an OnlyBest Creator"
            title="You're more than a referrer — you're a local connector."
            subtitle="You are not just sending a link. You are becoming a trusted local connector, promoter, and marketing support partner for the businesses you believe in."
          />
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            {responsibilities.map((r) => (
              <div
                key={r}
                className="flex items-start gap-3 rounded-xl bg-white/[0.03] border border-white/10 p-4 hover:border-[#F59E0B]/30 transition"
              >
                <Check className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#E2E8F0] leading-relaxed">{r}</span>
              </div>
            ))}
          </div>
        </div>

        {/* COST + UNLOCK */}
        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/10 to-transparent p-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#10B981] mb-3">
              What it costs to join
            </p>
            <h3 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
              Start for Free
            </h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">
              OnlyBest Creators can start for free. You receive:
            </p>
            <ul className="space-y-2.5">
              {freeIncludes.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-[#E2E8F0]">
                  <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-[#64748B] italic">
              There is no required monthly fee to begin.
            </p>
          </div>

          <div className="rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 to-transparent p-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] mb-3">
              When you unlock full platform access
            </p>
            <h3 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
              Get 1 active client → unlock more
            </h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed">
              Once you recommend and sign up at least one active client, you unlock
              expanded platform access — similar to a Best of Best business or agent,
              including access to more of the AI Horse-Powered platform, CRM tools,
              support systems, promotional tools, and creator resources.
            </p>
            <p className="mt-4 text-sm text-[#94A3B8] leading-relaxed">
              To keep full platform access active, you must have at least one active
              client you personally referred or are assigned to support.
            </p>
          </div>
        </div>

        {/* HOW ONLYBEST CREATORS GET PAID — single unified comp table */}
        <div className="mt-20">
          <SectionHeading
            kicker="Compensation"
            title="How OnlyBest Creators Get Paid"
            subtitle="One system. Non-selling. Recurring by design."
          />
          <CompTable />
          <p className="mt-5 text-xs text-[#94A3B8] max-w-4xl mx-auto leading-relaxed">
            Certified Creator = completed certification + 3 activated businesses. Commissions are paid on
            collected payments after a 30-day hold, monthly. Commissions on refunded payments (within 60 days)
            are reversed. Recurring commissions continue while the business stays active and you remain in
            good standing. No commission is paid on Co-Op activation/funding fees.
          </p>

          <div className="mt-6 max-w-4xl mx-auto rounded-2xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 to-transparent p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#F59E0B] mb-2">Example math (not typical results)</p>
            <p className="text-sm text-[#E2E8F0] leading-relaxed">
              3 businesses on the $1,997 plan with 4 agent seats = <strong className="text-white">about $1,198 upfront</strong> +{" "}
              <strong className="text-white">about $798/mo recurring</strong> while they stay active.
            </p>
          </div>
        </div>

        {/* WHAT YOUR BUSINESSES GET */}
        <div className="mt-20 max-w-4xl mx-auto rounded-3xl border border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/10 via-[#0F172A] to-[#DC2626]/10 p-7 sm:p-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#2563EB] mb-3">What Your Businesses Get</p>
          <h3 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] leading-tight">
            Everything a modern local business needs to grow — handled.
          </h3>
          <p className="mt-4 text-base text-[#CBD5E1] leading-relaxed">
            Roe Hubbard as their <span className="text-white font-semibold">Fractional Chief AI & Marketing Officer</span> and the full
            AI Horse-Powered team, plus CRM, AI agents, daily content, video, leads, a LocalBest.ai listing, and the complete
            AI Horse-Powered platform. Approved businesses may receive up to{" "}
            <span className="text-[#10B981] font-semibold">$120,000 in Business Co-Op Credits</span> applied toward AI
            Horse-Powered services.
          </p>
        </div>

        {/* SUPPORT YOU RECEIVE */}
        <div className="mt-20">
          <SectionHeading
            kicker="What Support You Receive"
            title="You are not alone. You are part of a creator-powered movement."
          />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {supportItems.map((s) => (
              <div
                key={s}
                className="flex items-start gap-3 rounded-xl bg-white/[0.03] border border-white/10 p-4"
              >
                <ShieldCheck className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#E2E8F0] leading-relaxed">{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* WHO THIS IS FOR */}
        <div className="mt-20">
          <SectionHeading
            kicker="Who This Is For"
            title="The OnlyBest Creator Program is ideal for…"
            subtitle="You do not need to be a tech expert. You need to be willing to promote good businesses, help them get active, support their visibility, and use the tools and training provided."
          />
          <div className="mt-10 flex flex-wrap gap-2 justify-center">
            {whoFor.map((w) => (
              <span
                key={w}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm text-[#E2E8F0]"
              >
                <Check className="w-3.5 h-3.5 text-[#F59E0B]" /> {w}
              </span>
            ))}
          </div>
        </div>

        {/* BIG PICTURE + FINAL JOIN FREE */}
        <div className="mt-20 max-w-4xl mx-auto rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 via-[#0F172A] to-[#EC4899]/10 p-7 sm:p-10 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] mb-4">
            The Big Picture
          </p>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] leading-tight">
            A normal creator gets paid for views & likes.
            <br className="hidden sm:block" />{" "}
            An OnlyBest Creator builds recurring income by helping real businesses grow.
          </h3>
          <p className="mt-5 text-base text-[#CBD5E1] leading-relaxed">
            Start free. Get your Creator Listing. Recommend great businesses. Help
            them become Best of Best. Earn upfront and monthly.
          </p>
          <p className="mt-2 text-lg text-white font-semibold">
            Promote good. Earn more. Change lives.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/onlybest/apply" data-testid="creator-program-join-free-bottom">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold rounded-full px-8 py-6 text-base shadow-lg shadow-[#F59E0B]/30"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Join Free — Apply Now
              </Button>
            </Link>
          </div>
          <p className="mt-5 text-[11px] text-[#64748B] max-w-xl mx-auto">
            After you submit your application, we may call to verify your identity
            and you&apos;ll receive an email from LocalBest.ai with onboarding instructions.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- COMP TABLE (single source of truth) ---------------- */
function CompTable() {
  const rows = [
    {
      what: "Business applies ($1)",
      creator: "$0",
      certified: "$0",
    },
    {
      what: "Business activates a $997 / $1,997 / $3,997 monthly plan",
      creator: (
        <>
          <strong className="text-white">20%</strong> of first collected month
          <br />
          <span className="text-xs text-[#94A3B8]">($199 / $399 / $799)</span>
        </>
      ),
      certified: (
        <>
          <strong className="text-white">40%</strong> of first collected month
          <br />
          <span className="text-xs text-[#94A3B8]">($399 / $799 / $1,599)</span>
        </>
      ),
    },
    {
      what: "Every month the business stays active",
      creator: (
        <>
          <strong className="text-white">10%</strong> recurring
          <br />
          <span className="text-xs text-[#94A3B8]">($100 / $200 / $400)</span>
        </>
      ),
      certified: (
        <>
          <strong className="text-white">10%</strong> recurring
        </>
      ),
    },
    {
      what: "Agent seats ($497/mo, added under a business plan)",
      creator: (
        <>
          <strong className="text-white">10%</strong> recurring per seat
          <br />
          <span className="text-xs text-[#94A3B8]">($49.70)</span>
        </>
      ),
      certified: (
        <>
          <strong className="text-white">10%</strong> recurring per seat
        </>
      ),
    },
    {
      what: "Videos & content you produce for your businesses",
      creator: "Paid per video ordered in the Member Portal",
      certified: "Paid per video",
    },
  ];

  return (
    <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="comp-table">
      <table className="w-full text-left text-sm">
        <thead className="bg-white/[0.04]">
          <tr>
            <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#94A3B8] font-semibold w-1/2">What happens</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#DC2626] font-semibold">Creator</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#F59E0B] font-semibold">
              <span className="inline-flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5" /> Certified Creator
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/5 align-top">
              <td className="px-4 py-4 text-[#E2E8F0]">{r.what}</td>
              <td className="px-4 py-4 text-[#CBD5E1]">{r.creator}</td>
              <td className="px-4 py-4 text-[#CBD5E1]">{r.certified}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- DISCLOSURES (footer of /onlybest) ---------------- */
function Disclosures() {
  const items = [
    "Earnings depend on your effort and on businesses activating and staying active. Earnings are not guaranteed. Examples are illustrations, not typical results.",
    "Business Co-Op Credits are applied toward AI Horse-Powered services. They are not a loan, cash, or grant. Approval and amounts vary by business.",
    "OnlyBest Creators must clearly disclose that they are compensated when promoting businesses (for example #ad or \"I earn a commission\").",
  ];
  return (
    <section className="py-16 border-t border-white/5 bg-[#0A0F1C]" data-testid="onlybest-disclosures">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.3em] text-[#64748B] mb-4 text-center">Disclosures</p>
        <ul className="space-y-3 text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
          {items.map((t, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#64748B] mt-0.5">•</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PayCard({ tag, title, bullets, highlight, tone, border, accent }) {
  return (
    <div
      className={`relative rounded-2xl border ${border} bg-gradient-to-br ${tone} bg-[#0F172A] p-6 min-w-0`}
    >
      <p className={`text-[11px] uppercase tracking-[0.18em] ${accent} mb-2`}>{tag}</p>
      <h4 className="text-white font-semibold text-lg leading-snug mb-4">{title}</h4>
      <ul className="space-y-2 mb-4">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-[#CBD5E1]">
            <Check className={`w-4 h-4 ${accent} flex-shrink-0 mt-0.5`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="rounded-xl bg-white/[0.04] border border-white/5 p-3">
        <p className={`text-sm font-semibold ${accent}`}>{highlight}</p>
      </div>
    </div>
  );
}

function ExampleCard({ label, title, rows, total, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 min-w-0">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] mb-2">{label}</p>
      <h4 className="text-white font-semibold text-lg mb-4">{title}</h4>
      <div className="space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-[#94A3B8]">{k}</span>
            <span className="text-white font-medium">{v}</span>
          </div>
        ))}
      </div>
      <div
        className={`mt-5 rounded-xl bg-gradient-to-r ${accent} px-4 py-3 text-center`}
      >
        <p className="text-sm font-semibold text-white">{total}</p>
      </div>
    </div>
  );
}

/* ---------------- ONLYBEST STREAMER TEASER (now a small inline link) ---------------- */
function OnlyBestStreamerTeaser() {
  return (
    <section className="py-10 border-t border-white/5" data-testid="onlybest-streamer-teaser-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Link
          to="/onlybest-streamers"
          className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white transition"
          data-testid="onlybest-streamer-cta"
        >
          <Video className="w-4 h-4 text-[#DC2626]" />
          Streamer? See OnlyBest Live
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

/* ---------------- SECTION HEADING ---------------- */
function SectionHeading({ kicker, title, subtitle }) {
  return (
    <div className="max-w-3xl mx-auto text-center">
      {kicker ? <p className="text-xs uppercase tracking-[0.3em] text-[#DC2626] mb-3">{kicker}</p> : null}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] leading-tight">{title}</h2>
      {subtitle ? <p className="mt-3 text-base sm:text-lg text-[#94A3B8] leading-relaxed">{subtitle}</p> : null}
    </div>
  );
}
